/**
 * Report Settings Store
 *
 * Caches workspace report settings and exposes actions for toggling
 * visibility and required status.
 */

import { create } from 'zustand';
import type { AppArea } from '../../lib/appAreas';
import {
  listReportSettings,
  setReportVisible,
  setReportRequired,
  setReportAreas,
  type WorkspaceReportSetting,
} from '../../lib/reportSettingsService';

interface ReportSettingsState {
  settings: WorkspaceReportSetting[];
  loading: boolean;
  error: string | null;
  loadedWorkspaceId: string | null;

  fetchSettings: (workspaceId: string) => Promise<void>;
  /** Returns the setting for a given report key, or a sensible default. */
  getSetting: (reportKey: string) => { isVisible: boolean; isRequired: boolean };
  toggleVisible: (workspaceId: string, reportKey: string) => Promise<void>;
  toggleRequired: (workspaceId: string, reportKey: string) => Promise<void>;
  /** Where the report appears in this workspace; null returns it to the platform's placement. */
  setAreas: (workspaceId: string, reportKey: string, areas: AppArea[] | null) => Promise<void>;
}

export const useReportSettingsStore = create<ReportSettingsState>((set, get) => ({
  settings: [],
  loading: false,
  error: null,
  loadedWorkspaceId: null,

  fetchSettings: async (workspaceId) => {
    if (get().loadedWorkspaceId === workspaceId && get().settings.length > 0) return;
    set({ loading: true, error: null });
    const { data, error } = await listReportSettings(workspaceId);
    set({ settings: data, loading: false, error, loadedWorkspaceId: workspaceId });
  },

  getSetting: (reportKey) => {
    const s = get().settings.find((r) => r.reportKey === reportKey);
    return s ? { isVisible: s.isVisible, isRequired: s.isRequired } : { isVisible: true, isRequired: false };
  },

  toggleVisible: async (workspaceId, reportKey) => {
    const current = get().getSetting(reportKey);
    if (current.isRequired) return; // Required reports cannot be hidden
    const next = !current.isVisible;

    // Optimistic update
    set((state) => ({
      settings: state.settings.map((s) =>
        s.reportKey === reportKey ? { ...s, isVisible: next } : s
      ),
    }));

    const { error } = await setReportVisible(workspaceId, reportKey, next);
    if (error) {
      // Rollback
      set((state) => ({
        settings: state.settings.map((s) =>
          s.reportKey === reportKey ? { ...s, isVisible: current.isVisible } : s
        ),
        error,
      }));
    }
  },

  toggleRequired: async (workspaceId, reportKey) => {
    const current = get().getSetting(reportKey);
    const next = !current.isRequired;

    set((state) => ({
      settings: state.settings.map((s) =>
        s.reportKey === reportKey
          ? { ...s, isRequired: next, isVisible: next ? true : s.isVisible }
          : s
      ),
    }));

    const { error } = await setReportRequired(workspaceId, reportKey, next);
    if (error) {
      set((state) => ({
        settings: state.settings.map((s) =>
          s.reportKey === reportKey ? { ...s, isRequired: current.isRequired } : s
        ),
        error,
      }));
    }
  },

  setAreas: async (workspaceId, reportKey, areas) => {
    const before = get().settings;
    const exists = before.some((s) => s.reportKey === reportKey);
    const now = new Date().toISOString();
    set({
      settings: exists
        ? before.map((s) => (s.reportKey === reportKey ? { ...s, areas } : s))
        : [...before, { id: `local-${reportKey}`, workspaceId, reportKey, isVisible: true, isRequired: false, areas, createdAt: now, updatedAt: now }],
    });
    const { error } = await setReportAreas(workspaceId, reportKey, areas);
    if (error) set({ settings: before, error });
  },
}));
